import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  AutoSizer,
  Column,
  defaultTableRowRenderer,
  InfiniteLoader,
  Table as RVTable,
} from 'react-virtualized';

import { isTruthy } from 'lib/Helpers';
import {
  getData as getTableData,
  resetTableCache as resetCache,
  updateSort as updateColumnSort,
} from 'modules/Table';
import withRowRenderer from './hoc/withRowRenderer';

import CustomHeaderRowRenderer from './CustomHeaderRenderers/CustomHeaderRowRenderer';
import CustomHeaderMenuRowRenderer from './CustomHeaderRenderers/CustomHeaderMenuRowRenderer';
import LoadingRow from './LoadingRow';
import * as styles from './Table.scss';

/**
 * @class Table
 * @classdesc Reusable Table Component
 * @property {Object} state
 * @property {Object} loaderRef
 * @return {ReactElement}
 * @example ./Table.md
 */

class Table extends Component {
  static propTypes = {
    columns: PropTypes.arrayOf(
      PropTypes.objectOf(
        PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
          PropTypes.func,
          PropTypes.bool,
        ])
      ),
    ).isRequired,
    data: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string,
        value: PropTypes.any,
        className: PropTypes.string,
        style: PropTypes.object,
      }),
    ).isRequired,
    getData: PropTypes.func.isRequired,
    resetTableCache: PropTypes.func.isRequired,
    tableKey: PropTypes.string.isRequired,
    tableState: PropTypes.objectOf(
      PropTypes.shape({
        reset: PropTypes.bool,
      }),
    ).isRequired,
    updateSort: PropTypes.func.isRequired,
    customRowRenderer: PropTypes.func,
    hasRowMenus: PropTypes.bool,
    headerHeight: PropTypes.number,
    headerRowRenderer: PropTypes.func,
    headerRowStyle: PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]),
    ),
    isInfinite: PropTypes.bool.isRequired,
    rowHeight: PropTypes.number,
    rowStyle: PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]),
    ),
    size: PropTypes.number,
    threshold: PropTypes.number,
    totalDataLength: PropTypes.number,
  };

  static defaultProps = {
    customRowRenderer: null,
    rowStyle: {},
    hasRowMenus: false,
    headerHeight: 40,
    headerRowRenderer: null,
    headerRowStyle: {},
    rowHeight: 48,
    size: 50,
    threshold: 15,
    totalDataLength: Number.MAX_SAFE_INTEGER,
  };

  constructor(props) {
    super(props);
    this.loaderRef = React.createRef();
  }

  componentDidUpdate() {
    const { tableKey } = this.props;
    const { resetTableCache, tableState } = this.props;
    /**
     * This allows some tables to go unregistered.
     * We have simple use-cases for <Table /> components that do not require the Redux methods for resetting.
     * Prevents error that occurs when searching for reset flag for an unregistered table in a nested object structure.
    */
    const reset = isTruthy(tableState[tableKey]) ? tableState[tableKey].reset : null;

    if (isTruthy(reset)) {
      this.loaderRef.current.resetLoadMoreRowsCache();
      resetTableCache(tableKey, false);
      return this.loadMoreRows({ startIndex: 0, stopIndex: 0 });
    }
    return null;
  }

  /**
   * Handle Column Sorting When Header Clicked
   * @param {Object}
   * @prop {String} sortBy
   * @prop {String} sortDirection
   * @return {Function} props.updateSort
   */
  handleSort = ({ sortBy, sortDirection }) => {
    const { tableKey, tableState, updateSort } = this.props;
    const { sortBy: prevSortBy } = tableState[tableKey];

    return sortBy === prevSortBy
      ? updateSort({ key: tableKey, sortBy, sortDirection })
      : updateSort({ key: tableKey, sortBy, sortDirection: 'ASC' });
  };

  /**
   * Fetches Row Count
   * @description Row count must be 1 greater than data length to load more rows.
   * @see react-virtualized docs loadMoreRows definition
   * @return {Number}
   */
  getRowCount = () => {
    const { isInfinite, data, totalDataLength } = this.props;

    if (!isInfinite) return data.length;
    return data.length < totalDataLength
      ? data.length + 1
      : data.length;
  };

  /**
   * Gets row at index for table
   * @param {number} - The current row index
   * @return {Object} - Data at row index or empty object
   */
  rowGetter = ({ index }) => {
    const { data } = this.props;

    if (!this.isRowLoaded({ index })) return {};
    return data[index];
  };

  /**
   * Checks if Row is Loaded
   * @param {number} index
   * @return {boolean}
   */
  isRowLoaded = ({ index }) => {
    const { data } = this.props;
    return !!data[index];
  };

  /**
   * Loads More Rows
   * @param {number} startIndex
   * @param {number} stopIndex
   * @return {Function} this.props.getRows
   */
  loadMoreRows = ({ startIndex, stopIndex }) => {
    const { getData, size, tableKey } = this.props;

    this._loadMoreRowsStartIndex = startIndex;
    this._loadMoreRowsStopIndex = stopIndex;
    return getData(tableKey, size);
  };

  headerRowRenderer = renderProps => {
    const { hasRowMenus, headerRowRenderer, headerRowStyle } = this.props;
    if (isTruthy(headerRowRenderer)) return headerRowRenderer(renderProps);
    return hasRowMenus
      ? CustomHeaderMenuRowRenderer(renderProps)
      : CustomHeaderRowRenderer({ ...renderProps, headerRowStyle });
  };

  /**
   * Render Row Function
   * @param {Object} data
   * @return {ReactElement} DefaultTableRowRenderer
   */
  rowRenderer = data => {
    const { index } = data;
    const { customRowRenderer } = this.props;
    const DefaultTableRowRenderer = withRowRenderer(defaultTableRowRenderer);

    if (!this.isRowLoaded({ index })) {
      /* Fixes bug where <LoadingRow /> would appear in middle of table if scrolling up after fetching new data */
      if (data.isScrolling) return null;
      return (<LoadingRow {...data} />);
    }

    if (customRowRenderer) return customRowRenderer(data);
    return <DefaultTableRowRenderer {...data} />;
  };

  noRowsRenderer = () => {
    return (
      <div className={styles.noRows}>
        <span className={styles.noRowsText}>No results found</span>
      </div>
    );
  };

  render() {
    const {
      columns,
      headerHeight,
      rowHeight,
      rowStyle,
      tableKey,
      tableState,
      threshold,
      sort,
    } = this.props;

    /* Nested Payload. Confirm We Can Destructure Without Error or Default to Empty Object */
    const tableProps = isTruthy(tableState) && isTruthy(tableState[tableKey]) ? tableState[tableKey] : {};
    const { sortBy, sortDirection } = tableProps;
    return (
      <div className={styles.tableContainer}>
        <InfiniteLoader
          isRowLoaded={this.isRowLoaded}
          loadMoreRows={this.loadMoreRows}
          threshold={threshold}
          ref={this.loaderRef}
          rowCount={this.getRowCount()}
        >
          {({ onRowsRendered, registerChild }) => (
            <AutoSizer>
              {({ height, width }) => (
                <RVTable
                  headerHeight={headerHeight}
                  headerRowRenderer={this.headerRowRenderer}
                  headerClassName="headerStyle"
                  headerStyle={{ paddingRight: 0 }}
                  height={height}
                  noRowsRenderer={this.noRowsRenderer}
                  onRowsRendered={onRowsRendered}
                  overscanRowCount={2}
                  ref={registerChild}
                  rowClassName="rowStyle"
                  rowCount={this.getRowCount()}
                  rowGetter={this.rowGetter}
                  rowRenderer={this.rowRenderer}
                  rowStyle={rowStyle}
                  rowHeight={rowHeight}
                  sort={sort}
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  width={width}
                >
                  {columns.map((column) => <Column key={column.dataKey} {...column} />)}
                </RVTable>
              )}
            </AutoSizer>)}
        </InfiniteLoader>
      </div>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  getData: (key, size) => dispatch(getTableData(key, size)),
  resetTableCache: (key, bool) => dispatch(resetCache(key, bool)),
  updateSort: sortData => dispatch(updateColumnSort(sortData)),
});

const mapStateToProps = state => ({
  tableState: state.table,
});

const ConnectedTable = connect(mapStateToProps, mapDispatchToProps)(Table);

export { Table as PureTable };
export default ConnectedTable;
